import { useForm } from "react-hook-form";
import { Button, Input, Modal, Numeric, Textarea } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { formatGBP } from "../lib/format.js";
import { useToast } from "../lib/toast.js";
import type {
  AdminOrderDetailDTO,
  RefundCreatedResponse,
} from "@wmart/shared-types";

interface RefundFormValues {
  amount_minor: string;
  reason: string;
}

interface Props {
  order: AdminOrderDetailDTO;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
}

const REASON_MAX = 500;

/**
 * Issue a refund. POST /v1/admin/refunds returns 202 Accepted (the
 * api-client treats 2xx identically). Body uses
 * `CreateRefundBody = { order_id, amount_minor, reason }`.
 *
 * UX: a "Refund full balance" pre-fill button computes the remaining
 * balance (`total_minor - refunded_minor`) and seeds the amount input.
 * 422 `order.refund_exceeds_balance` and 409 `order.refund_in_progress`
 * surface via the toast provider.
 *
 * Rendered inside `<Modal>` (Plan 11 T11) — ESC + click-outside +
 * focus-trap + scroll-lock all come from the primitive.
 */
export function RefundModal({ order, onSuccess, onClose }: Props) {
  const { notify, notifyProblem } = useToast();
  const remainingBalance = order.total_minor - order.refunded_minor;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<RefundFormValues>({
    defaultValues: { amount_minor: "", reason: "" },
  });

  const reason = watch("reason");

  function fillFullBalance() {
    setValue("amount_minor", String(remainingBalance), {
      shouldValidate: true,
    });
  }

  async function onSubmit(values: RefundFormValues) {
    const amountMinor = Number(values.amount_minor);
    if (!Number.isInteger(amountMinor) || amountMinor <= 0) {
      notify("error", "Amount must be a positive integer (pence)");
      return;
    }
    try {
      await api.post<RefundCreatedResponse>("/v1/admin/refunds", {
        order_id: order.id,
        amount_minor: amountMinor,
        reason: values.reason,
      });
      notify("success", "Refund initiated");
      await onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Refund failed");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Issue refund"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Issue refund
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <div className="rounded-sm border border-ink-200 bg-paper-warm p-4 text-body-sm">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-ink-500">Order total</dt>
            <dd className="text-right text-ink-900">
              <Numeric serif>{formatGBP(order.total_minor)}</Numeric>
            </dd>
            <dt className="text-ink-500">Already refunded</dt>
            <dd className="text-right text-ink-900">
              <Numeric serif>{formatGBP(order.refunded_minor)}</Numeric>
            </dd>
            <dt className="text-ink-700 font-medium">Remaining balance</dt>
            <dd className="text-right text-ink-900 font-medium">
              <Numeric serif>{formatGBP(remainingBalance)}</Numeric>
            </dd>
          </dl>
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              type="number"
              min={1}
              step={1}
              label="Amount (pence)"
              helper="Minor units (pence). Must not exceed remaining balance."
              error={errors.amount_minor?.message}
              {...register("amount_minor", {
                required: "Amount is required",
              })}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={fillFullBalance}
            disabled={remainingBalance <= 0}
            className="mb-6 whitespace-nowrap"
          >
            Refund full balance
          </Button>
        </div>

        <Textarea
          label="Reason"
          rows={3}
          maxLength={REASON_MAX}
          helper={`${reason.length}/${REASON_MAX}`}
          error={errors.reason?.message}
          {...register("reason", {
            required: "Reason is required",
            minLength: { value: 1, message: "Reason cannot be empty" },
            maxLength: {
              value: REASON_MAX,
              message: `Max ${REASON_MAX} characters`,
            },
          })}
        />

        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
